import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import { 
    Box,
    MenuItem,
    Stack,
    TextField,
    Button,
    Paper,
    Alert,
    Snackbar,
    Autocomplete,
    Checkbox
} from "@mui/material"

import { Header } from "../../components/Header/Header"
import { FilesSelector } from "../../components/FilesSelector/FilesSelector"
import { PDialog } from "../../components/PDialog/PDialog"

import { strings } from "../../config/strings"

import { getFileName, parseCSV } from "../../utils/csvUtils"

import { useAuth } from "../../hooks/useAuth"

import type { PMPPModel } from "../../types/Types"
import { createPredictionJob, getModels } from "../../utils/api"
import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material"

export const PredictionCreatePage = () => {
    
    //State

    const [loading, setLoading] = useState<boolean>(false)
    const [requestLoading, setRequestLoading] = useState<boolean>(false)

    const [openSuccessDialog, setOpenSuccessDialog] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string | undefined>()
    const [openPasteError, setOpenPasteError] = useState<boolean>(false)

    const [files, setFiles] = useState<File[]>([])

    const [predictionJobName, setPredictionJobName] = useState<string>('')

    const [columns, setColumns] = useState<string[]>([])
    const [selectedColumn, setSelectedColumn] = useState<string>('')

    const [models, setModels] = useState<PMPPModel[]>([])
    const [selectedModels, setSelectedModels] = useState<PMPPModel[]>([]) 


    //Hooks 

    const navigate = useNavigate()
    const auth = useAuth()

    //Callbacks

    const loadData = useCallback( async () => {

        try {
            setLoading(true)
            const models = await getModels()

            setModels(models)
        }
        catch(e:any){
            setErrorMessage(e.data)
        }
        finally {
            setLoading(false)
        }

    }, [])


    //Effects

    useEffect( () => {
        loadData()
    }, [])

    useEffect( () => {

        const handlePaste = (event: ClipboardEvent) => {
            const target = event.target as HTMLElement | null;

            if (
                target && (
                    target.tagName === "INPUT" ||
                    target.tagName === "TEXTAREA" ||
                    target.isContentEditable
                )
            ) {
                return
            }

            const text = event.clipboardData?.getData("text") ?? ""
            const res = parseCSV(text)
            
            if(res.valid) {

                const file = new File([text], 'user_pasted.csv', {
                    type: "text/csv;charset=utf-8"
                })
                
                handleFilesSelected([file])
            }
            else {
                setOpenPasteError(true)
            }
        }

        window.addEventListener("paste", handlePaste)

        return () => {
            window.removeEventListener("paste", handlePaste)
        }
    }, [])


    //Handlers

    const handleFilesSelected = (files:File[]) => {

        if(files && files.length){
            setFiles(files)

            const reader = new FileReader()

            reader.onload = () => {
                const text = reader.result as string
                const parsed = parseCSV(text)
    
                setColumns(parsed.headers)
                
                const smilesColumn = parsed.headers.find(s => /smiles/i.test(s))
                setSelectedColumn(smilesColumn??'')

                if((!predictionJobName || predictionJobName.length === 0) && auth.getCurrentUser()) {

                    setPredictionJobName( `${auth.getCurrentUser()?.full_name.split(' ')[0]}_${getFileName(files[0])}` )
                }
            }

            reader.onerror = () => {
                console.error("Error reading file", reader.error);
            }

            reader.readAsText(files[0], "utf-8");
        } 
    }

    const handleFileDelete = (file: File) => {
        setFiles(s => s.filter( f => f !== file))
        setColumns([])
        setSelectedColumn("")
    } 

    const handleSubmitClick = async () => {

        if(
            !files || 
            files.length === 0 || 
            (!selectedColumn  && files[0].name.endsWith('csv')) ||
            selectedModels.length === 0
        ) return

        try {
            setRequestLoading(true)

            const createPredictioRes = await createPredictionJob(
                files[0], 
                predictionJobName, 
                selectedModels.map( model => model.name), 
                (selectedColumn.length > 0)? selectedColumn : "SMILES"
            )
            
            if(createPredictioRes){
                setOpenSuccessDialog(true)
            }
        }
        catch(e:any){
            setErrorMessage(e.data)
        }
        finally {
            setRequestLoading(false)
        }
    }

    //Render

    const renderColumnSelector = () => (
            files && files[0] && files[0].name.toLowerCase().endsWith(".csv") && 
            <TextField
                select
                label={strings.smiles_column}
                disabled={!files || files.length === 0}
                value={selectedColumn}
                onChange={(e) => setSelectedColumn(e.target.value)}
            >
                {
                    columns.map( (column) => <MenuItem key={column} value={column}>{column}</MenuItem>)
                }
            </TextField>
    )


    const renderModelsSelector = () => {

        const icon = <CheckBoxOutlineBlank fontSize="small" />
        const checkedIcon = <CheckBox fontSize="small" />

        return (
            <Autocomplete
                multiple
                disableCloseOnSelect
                options={models}
                value={selectedModels}
                onChange={(_, newValue) => setSelectedModels(newValue)}
                getOptionLabel={(o) => o.name.replace(".pkl", "")}
                renderOption={(props, option, { selected }) => (
                    <li {...props} key={option.name}>
                        <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                        />
                        {option.name.replace(".pkl", "")}
                    </li>
                )}
                renderInput={(params) => <TextField name="models" {...params} label={strings.properties_predictions} />}
                />
            )
    }

    return(
        <Box maxWidth='90%' margin='auto' py={2}>
            <Stack spacing={2}>
                <Header 
                    title={strings.new_prediction} 
                    breadcrumbs={[{
                        title: strings.prediction_jobs,
                        link: '/'
                    }]}
                />
                <Paper sx={{p:2}}>
                    <Stack spacing={4}> 
                        <FilesSelector 
                            accept=".csv,.sdf" 
                            files={files}
                            onFilesSelected={handleFilesSelected}
                            onFileDelete={handleFileDelete}
                            errorMessage={strings.only_csv_file_allowed}
                        />
                        <TextField
                            required
                            label={strings.prediction_job_name}
                            value={predictionJobName}
                            onChange={(e) => setPredictionJobName(e.target.value)}
                            autoComplete="off"
                        />
                        {renderColumnSelector()}
                        {renderModelsSelector()}
                        <Button 
                            type='submit' 
                            variant='contained'
                            disabled={
                                loading ||
                                !files || 
                                files.length === 0 || 
                                (selectedColumn === '' && files[0].name.endsWith('csv')) || 
                                !predictionJobName ||
                                predictionJobName.length < 3 ||
                                selectedModels.length === 0
                            }
                            loading={requestLoading}
                            onClick={handleSubmitClick}
                        >
                            {strings.action_submit}
                        </Button>
                        <PDialog 
                            open={openSuccessDialog} 
                            onClose={() => setOpenSuccessDialog(false)} 
                            onSubmit={() => {
                                navigate('/')
                            }}
                            submitButtonLabel={strings.ok}
                            hideCancelButton
                            title={strings.prediction_job_created}
                            body={strings.prediction_job_successfully_created}
                        />
                        <Snackbar 
                            open={Boolean(errorMessage)} 
                            autoHideDuration={6000} 
                            onClose={() => setErrorMessage(undefined)}
                        >
                            <Alert severity="error">
                                {`${strings.request_error}: ${errorMessage}`}
                            </Alert>
                        </Snackbar>
                    </Stack>
                </Paper>
            </Stack>
            <Snackbar 
                open={openPasteError} 
                onClose={() => setOpenPasteError(false)}
            >
                <Alert severity="error">{strings.invalid_pasted_csv}</Alert>
            </Snackbar>
        </Box>
    )
}